import axios from "axios"
const apiKey = "IGzAum4OiB5eGFDxXyFW7es6J15f3RPCQ1BOrhwP";
export interface StatisticalData {
  mean: number
  standardDeviation: number
  skewness: number
  median: number
  min: number
  max: number
  kurtosis: number
}

export interface DepartmentPerformanceData {
  id: string
  name: string
  employees: number
  statistics: StatisticalData
  ratings: number[]
  submissionStatus: {
    submitted: number
    approved: number
    pending: number
    total: number
  }
  kpiPerformance: {
    top3: Array<{
      name: string
      score: number
      trend: "up" | "down" | "stable"
    }>
    bottom3: Array<{
      name: string
      score: number
      trend: "up" | "down" | "stable"
    }>
  }
  targetProgress: number
}

export interface AIInsightResponse {
  overallAssessment: string
  departmentInsights: Array<{
    department: string
    strengths: string[]
    concerns: string[]
    recommendations: string[]
  }>
  statisticalFindings: string
  actionItems: string[]
}

export const askCohere = async (prompt: string) => {
  const response = await axios.post(
    "https://api.cohere.ai/v1/chat",
    {
      message: prompt,
      model: "command-r-plus", // or "command-r"
      temperature: 0.4,
      chat_history: [],
    },
    {
      headers: {
        Authorization: `Bearer ${apiKey}`, // Using environment variable
        "Content-Type": "application/json",
      },
    },
  )
  return response.data.text
}

export class PerformanceReportsService {
  static async generateAIInsights(
    departmentData: DepartmentPerformanceData[],
    organizationStats: StatisticalData,
  ): Promise<AIInsightResponse> {
    const prompt = this.buildAnalysisPrompt(departmentData, organizationStats)
    try {
      const response = await axios.post(
        "https://api.cohere.ai/v1/chat",
        {
          message: prompt,
          model: "command-r-plus",
          temperature: 0.4,
          chat_history: [],
        },
        {
          headers: {
            Authorization: `Bearer ${apiKey}`, // Using environment variable
            "Content-Type": "application/json",
          },
        },
      )
      return this.parseAIResponse(response.data.text)
    } catch (error) {
      console.error("AI Analysis Error:", error)
      throw new Error("Failed to generate AI insights")
    }
  }

  private static buildAnalysisPrompt(
    departmentData: DepartmentPerformanceData[],
    organizationStats: StatisticalData,
  ): string {
    const departmentSummary = departmentData
      .map(
        (dept) =>
          `DEPARTMENT: ${dept.name}- Employees: ${dept.employees}- Average Rating: ${dept.statistics.mean.toFixed(2)}- Standard Deviation: ${dept.statistics.standardDeviation.toFixed(2)}- Skewness: ${dept.statistics.skewness.toFixed(2)} (${
            dept.statistics.skewness > 0 ? "Right-skewed" : dept.statistics.skewness < 0 ? "Left-skewed" : "Normal"
          })- Submission Status: ${dept.submissionStatus.submitted}/${
            dept.submissionStatus.total
          } submitted, ${dept.submissionStatus.approved} approved- Target Progress: ${dept.targetProgress.toFixed(1)}%- Top KPIs: ${dept.kpiPerformance.top3
            .map((k) => `${k.name} (${k.score.toFixed(2)})`)
            .join(", ")}- Bottom KPIs: ${dept.kpiPerformance.bottom3
            .map((k) => `${k.name} (${k.score.toFixed(2)})`)
            .join(", ")}    `,
      )
      .join("\n")
    return `You are a performance management AI analyst. Analyze the following organizational and departmental performance data:ORGANIZATION STATISTICS:- Mean Performance: ${organizationStats.mean.toFixed(2)}- Standard Deviation: ${organizationStats.standardDeviation.toFixed(2)}- Median: ${organizationStats.median.toFixed(2)}- Performance Range: ${organizationStats.min.toFixed(2)} - ${organizationStats.max.toFixed(2)}- Skewness: ${organizationStats.skewness.toFixed(2)}- Kurtosis: ${organizationStats.kurtosis.toFixed(2)}DEPARTMENTAL BREAKDOWN:${departmentSummary}Please provide:1. Overall organizational performance assessment2. Department-specific insights with strengths, concerns, and recommendations3. Statistical findings and what they reveal about performance distribution4. Priority action items for leadershipFormat your response as a structured analysis with clear sections and actionable insights.`
  }

  private static parseAIResponse(response: string): AIInsightResponse {
    // Basic parsing - in production, you'd want more sophisticated parsing
    return {
      overallAssessment: response.substring(0, 300) + "...",
      departmentInsights: [],
      statisticalFindings: "AI analysis complete. See full report for detailed insights.",
      actionItems: [
        "Review departmental performance gaps",
        "Implement targeted interventions",
        "Monitor progress quarterly",
      ],
    }
  }
}
